import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import Select from '../../ui/Select';
import Input from '../../ui/Input';
import Button from '../../ui/Button';
import { SECTORS } from '../../../constants/authConstants';
import { departmentService } from '../../../services/departmentService';
import { financialYearUtils } from '../../../utils/helpers';

const FilterContent = ({ 
  config, 
  values, 
  onChange, 
  onApply,
  onClear,
  onClearAll,
  className = '',
  showApplyButton = false,
  layout = 'grid'
}) => {
  const { getResponsiveValue } = useTheme();
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  
  const gridCols = layout === 'grid' 
    ? getResponsiveValue({
        mobile: 'grid-cols-1',
        tablet: 'grid-cols-1',
        desktop: 'grid-cols-2'
      })
    : 'grid-cols-1';
  
  const gridGap = getResponsiveValue({
    mobile: 'gap-2',
    desktop: 'gap-3'
  });

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const allDepartments = await departmentService.getActiveDepartments();
      setDepartments(allDepartments);
    } catch (error) {
      console.error('Error loading departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const getFilterOptions = (filter) => {
    switch (filter.type) {
      case 'financialYear':
        return [
          { value: '', label: filter.allLabel || 'All Financial Years' },
          ...financialYearUtils.getFinancialYearOptions()
        ];
      
      case 'department':
        if (loadingDepartments) {
          return [{ value: '', label: 'Loading departments...' }];
        }
        
        const sortedDepartments = [...departments].sort((a, b) => 
          (a.priority || 999) - (b.priority || 999)
        );
        
        return [
          { value: '', label: filter.allLabel || 'All Departments' },
          ...sortedDepartments.map(dept => ({
            value: dept.departmentCode,
            label: dept.departmentName
          }))
        ];
      
      case 'sector':
        return [
          { value: '', label: filter.allLabel || 'All Sectors' },
          ...SECTORS.map(sector => ({ value: sector.value, label: sector.label }))
        ];
      
      case 'custom':
        return [
          { value: '', label: filter.allLabel || 'All' },
          ...(filter.options || [])
        ];
      
      default:
        return filter.options || [];
    }
  };

  const getUniqueOptions = (options, filterKey) => {
    const seen = new Set();
    return options.map((option, index) => {
      const originalValue = option.value;
      let uniqueValue = originalValue;
      
      if (seen.has(originalValue)) {
        uniqueValue = `${filterKey}-${originalValue}-${index}`;
      }
      seen.add(originalValue);
      
      return {
        ...option,
        value: originalValue,
        key: uniqueValue
      };
    });
  };

  const renderFilter = (filter) => {
    const commonProps = {
      label: filter.label,
      value: values[filter.key] || '',
      onChange: (e) => onChange(filter.key, e.target.value),
      className: "input-base",
      placeholder: filter.placeholder
    };

    switch (filter.component) {
      case 'select':
        const rawOptions = getFilterOptions(filter);
        const uniqueOptions = getUniqueOptions(rawOptions, filter.key);
        
        return (
          <Select
            key={filter.key}
            {...commonProps}
            options={uniqueOptions}
            disabled={filter.disabled || (filter.type === 'department' && loadingDepartments)}
            required={filter.required}
          />
        );
      
      case 'input':
        return (
          <Input
            key={filter.key}
            {...commonProps}
            type={filter.inputType || 'text'}
            placeholder={filter.placeholder}
          />
        );
      
      default:
        return null;
    }
  };

  const getActiveFilters = () => {
    return config.filter(filter => values[filter.key] && values[filter.key] !== '');
  };

  const activeFilters = getActiveFilters();

  const getDisplayValue = (filter) => {
    let displayValue = values[filter.key];
    
    if (filter.component === 'select') {
      const options = getFilterOptions(filter);
      const option = options.find(opt => opt.value === displayValue);
      displayValue = option?.label || displayValue;
    }
    
    return displayValue;
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-lg p-2 w-full max-w-full ${className}`}>
      <div className={`grid ${gridCols} ${gridGap} mb-2 w-full`}>
        {config.map(renderFilter)}
      </div>

      {activeFilters.length > 0 && (
        <div className="mb-3 p-2 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-blue-900">Active Filters</span>
            <span className="text-xs text-blue-600">
              {activeFilters.length} filter{activeFilters.length !== 1 ? 's' : ''}
            </span>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {activeFilters.map((filter) => (
              <span
                key={`active-${filter.key}`}
                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-white border border-blue-200 text-blue-800"
              >
                <span className="text-blue-600">{filter.label}:</span>
                <span className="ml-1 font-semibold">"{getDisplayValue(filter)}"</span>
                <button
                  onClick={() => onClear(filter.key)}
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 transition-colors"
                  title={`Clear ${filter.label}`}
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
        <button
          onClick={onClearAll}
          disabled={activeFilters.length === 0}
          className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Clear All
        </button>

        {showApplyButton && (
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={() => onApply && onApply()}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => onApply && onApply()}>
              Apply Filters
            </Button>
          </div>
        )}

        {!showApplyButton && (
          <span className="text-xs text-gray-500">
            Filters apply automatically
          </span>
        )}
      </div>
    </div>
  );
};

export default FilterContent;